/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import {Metric, MetricsEvents, RecordType} from '@exc/metrics';
import uuidv1 from 'uuid/v1';

/**
 * @enum
 * Event is an enumeration of the known page load events. These
 * events will be found in MetricsState column of a record metric
 * that represents event of the page in an active/done state
 * when the metric is created.
 */
export enum Events {
  HISTORY = 'History',
  LOAD_START = 'PageLoadStart',
  LOAD_DONE = 'PageLoadDone',
  SPINNER = 'Spinner',
  TIMEOUT = 'Timeout',
  WINDOW_LOCATION = 'WindowLocationChange',
}

/**
 * @enum
 * States represents the page state loading state.
 *
 * When the state is 'active', it means the page is still rendering/loading
 * from the last start event and no done event being found when the current
 * metric ingest to set the page to 'done'.
 *
 * When the state is 'done', it means the page is done with rendering/loading
 * from the last start event and no start event being found when the current
 * metric ingest to set the page to 'active'.
 */
enum States {
  ACTIVE = 'active',
  DONE = 'done',
  INIT = 'init', // construction time, this should never actually be sent to ADX
}

/**
 * @class
 * PageState represents the current PageState of an event in a sequence
 * of events.
 *
 * @property {number} epoch - Timestamp in milliseconds when a new start
 * event triggered.
 * @property {string} event - Holds the event data when a particular state
 * change occurred to trigger the state change.
 * @property {string} historyId - Helps identify the linear sequence of records
 * coming from start to end of a page state.
 * @property {string} state - The possible states of a page can be active
 * or done.
 */
export default class PageState {
  // TODO: maxTime should be picked up from runtime config after
  // metrics 1.0 is finalized to avoid compatibility issues.
  public static maxTime = 60000;
  public epoch = 0; // start at timeOrigin
  public event? = Events.LOAD_START;
  public historyId: string = uuidv1();
  public state: States = States.ACTIVE;
  private readonly now: () => number;
  private readonly setHref: () => void;
  private readonly hrefChanged: () => boolean;

  public constructor(adobeMetrics: AdobeMetricsRuntime, location?: Location) {
    this.now = (): number => adobeMetrics.now();
    this.setHref = (): void => {
      location?.href && (adobeMetrics.currentHRef = location.href);
    };
    this.hrefChanged = (): boolean => location?.href !== adobeMetrics.currentHRef;
  }
  /**
   * Set the page to active state will change the starting epoch, historyId,
   * and event at the same time.
   *
   * @param {Events} event The event trigger state change of the page.
   */
  public setPageActive(event: Events): void {
    this.epoch = this.now();
    this.event = event;
    this.historyId = uuidv1();
    this.state = States.ACTIVE;
    // TODO: If pageStage was active, emit an interrupted event
  }
  /**
   * Set the page to done state only resets the state and event. It will not
   * change the starting epoch or historyId.
   *
   * @param {Events} event The event trigger state change of the page.
   */
  public setPageDone(event: Events): void {
    this.event = event;
    this.state = States.DONE;
  }

  /**
   * Update the PageState instances based on current page state and the
   * existing metric props from user.
   *
   * @param {Metric} metric A metric record.
   */
  public handleChange(metric: Metric): void {
    delete this.event;
    switch (this.state) {
      case States.ACTIVE:
        this.checkIfDone(metric);
        break;
      case States.DONE:
      default:
        this.checkIfActive(metric);
        break;
    }
    this.setHref();
  }

  /**
   * Check to see if the metric has a specific event.
   *
   * @param {Metric} metric The metric record being checked.
   * @param {string} event The desired event.
   * @returns {boolean} True if the metric has the event.
   */
  private checkEvent(metric: Metric, event: string): boolean {
    if (Array.isArray(metric.event)) {
      return metric.event.indexOf(event) !== -1;
    } else {
      return event === metric.event;
    }
  }
  /**
   * Check the starting events of a page load. Any of starting events
   * is true will set the global runtime pageState object as active.
   *
   * @param {Metric} metric The metric record.
   */
  private checkIfActive(metric: Metric): void {
    const type = metric.recordType;
    // events that can trigger state to active sorted in order
    if (this.hrefChanged()) {
      this.setPageActive(Events.WINDOW_LOCATION);
      return;
    }
    switch (type) {
      case RecordType.HISTORY:
        this.setPageActive(Events.HISTORY);
        break;
      default:
        if (this.checkEvent(metric, MetricsEvents.PAGE_LOAD_START)) {
          this.setPageActive(Events.LOAD_START);
        }
        break;
    }
  }

  /**
   * Check the done events of a page load. Any of done events
   * is true will set the global runtime pageState object as done.
   *
   * @param {Metric} metric The metric record.
   */
  private checkIfDone(metric: Metric): void {
    if (this.now() - this.epoch > PageState.maxTime) {
      this.setPageDone(Events.TIMEOUT);
    } else if (this.checkEvent(metric, MetricsEvents.PAGE_LOAD_DONE)) {
      this.setPageDone(Events.LOAD_DONE);
    }
  }
}
